import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Field, FieldHint, Button, Callout } from '../ui';
import { Dialog } from '../ui/radix/Dialog';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Define item interface
interface ParcelItem {
  id: string;
  description: string;
  quantity: number;
  weight: number;
  price: number;
  madeIn: string;
}

interface ParcelProps {
  id: string;
  showPrice?: boolean;
  price?: number;
  currency?: string;
  quantity: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  physicalWeight: number;
  dimensionalWeight: number;
  unitSystem: 'metric' | 'imperial';
  fedexPackagingType?: string;
  onUpdate: (id: string, field: string, value: number) => void;
  onRemove: (id: string) => void;
}

const parcelSchema = z.object({
  quantity: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(1, 'Quantity must be at least 1')),
  weight: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Weight must be a positive number')),
  length: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Length must be a positive number')),
  width: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Width must be a positive number')),
  height: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Height must be a positive number')),
});

const itemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(1, 'Quantity must be at least 1')),
  weight: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Weight must be a positive number')),
  price: z.union([z.string(), z.number()]).pipe(z.coerce.number().min(0, 'Price must be a positive number')),
  madeIn: z.string().min(1, 'Country of origin is required'),
});

const expectedCurrency: string[] = ['ILS', 'USD', 'EUR', 'GBP'];

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
  'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
  'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
  'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
  'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay',
  'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen',
  'Zambia', 'Zimbabwe',
];

type ParcelFormValues = z.infer<typeof parcelSchema>;
type ItemFormValues = z.infer<typeof itemSchema>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--bg-elev)',
  color: 'var(--ink)',
  boxSizing: 'border-box',
};

const ParcelForm: React.FC<ParcelProps> = ({
  id,
  showPrice,
  price,
  currency = 'ILS',
  quantity,
  weight,
  length,
  width,
  height,
  physicalWeight,
  dimensionalWeight,
  unitSystem,
  fedexPackagingType,
  onUpdate,
  onRemove,
}) => {
  const isMetric = unitSystem === 'metric';
  const weightUnit = isMetric ? 'kg' : 'lb';
  const dimensionUnit = isMetric ? 'cm' : 'in';

  const areDimensionsDisabled = fedexPackagingType && fedexPackagingType !== 'YOUR_PACKAGING';

  const [items, setItems] = useState<ParcelItem[]>([]);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<ParcelItem | null>(null);
  const [totalItemsWeight, setTotalItemsWeight] = useState(0);
  const [weightWarning, setWeightWarning] = useState(false);

  const prevWeightRef = useRef(weight);
  const prevPriceRef = useRef(price || 0);
  const itemsWeightRef = useRef(0);
  const initialLoadComplete = useRef(false);

  const { control, reset, setValue } = useForm<ParcelFormValues>({
    resolver: zodResolver(parcelSchema),
    defaultValues: { quantity, weight, length, width, height },
    mode: 'onChange',
  });

  const { control: itemControl, handleSubmit: handleItemSubmit, reset: resetItemForm } = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: { description: '', quantity: 1, weight: 0, price: 0, madeIn: '' },
    mode: 'onChange',
  });

  const updateParentPrice = useCallback(
    (newPrice: number) => {
      if (newPrice !== prevPriceRef.current) {
        prevPriceRef.current = newPrice;
        console.log('Updating parent price:', newPrice);
        onUpdate(id, 'price', newPrice);
      }
    },
    [id, onUpdate]
  );

  useEffect(() => {
    reset({ quantity, weight, length, width, height });
    prevWeightRef.current = weight;
    prevPriceRef.current = price || 0;
    initialLoadComplete.current = true;
    if (physicalWeight > 0 || dimensionalWeight > 0) {
      setTotalItemsWeight(itemsWeightRef.current);
    }
  }, [reset, quantity, weight, length, width, height, price, physicalWeight, dimensionalWeight]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const itemsWeight = items.reduce((total, item) => total + item.weight * item.quantity, 0);
    setTotalItemsWeight(itemsWeight);
    itemsWeightRef.current = itemsWeight;
    const isOverweight = itemsWeight > weight;
    setWeightWarning(isOverweight);
    if (isOverweight && weight !== itemsWeight) {
      setValue('weight', itemsWeight, { shouldValidate: true });
      if (itemsWeight !== prevWeightRef.current) {
        prevWeightRef.current = itemsWeight;
        console.log('Updating parent weight:', itemsWeight);
        onUpdate(id, 'weight', itemsWeight);
      }
    }
    const totalPrice = items.reduce((total, item) => total + item.price * item.quantity, 0);
    if (totalPrice !== prevPriceRef.current) {
      prevPriceRef.current = totalPrice;
      updateParentPrice(totalPrice);
    }
  }, [items, weight, setValue, id, onUpdate, updateParentPrice]);

  const handleFieldChange = (field: string, value: number) => {
    if (value === undefined || isNaN(value) || value < 0) return;
    if (field === 'weight') {
      const iw = itemsWeightRef.current;
      if (items.length > 0 && value < iw) {
        setWeightWarning(true);
        value = iw;
      } else {
        setWeightWarning(false);
      }
    }
    if (field === 'weight' && value === prevWeightRef.current) return;
    onUpdate(id, field, value);
    console.log('Updating parent field:', field, value);
  };

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const timeoutId = setTimeout(() => {
      onUpdate(id, 'items', items as unknown as number);
      console.log('Updating parent items:', items);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, id]);

  const handleOpenItemDialog = (item?: ParcelItem) => {
    if (item) {
      setEditingItem(item);
      resetItemForm({ description: item.description, quantity: item.quantity, weight: item.weight, price: item.price, madeIn: item.madeIn });
    } else {
      setEditingItem(null);
      resetItemForm({ description: '', quantity: 1, weight: 0, price: 0, madeIn: '' });
    }
    setOpenItemDialog(true);
  };

  const handleCloseItemDialog = () => {
    setOpenItemDialog(false);
    setEditingItem(null);
  };

  const areItemsEqual = (item1: ParcelItem, item2: ParcelItem) =>
    item1.quantity === item2.quantity &&
    item1.weight === item2.weight &&
    item1.price === item2.price &&
    item1.description === item2.description &&
    item1.madeIn === item2.madeIn;

  const onItemSubmit = (data: ItemFormValues) => {
    if (editingItem) {
      if (!areItemsEqual(editingItem, { ...editingItem, ...data })) {
        setItems((prev) => prev.map((item) => (item.id === editingItem.id ? { ...item, ...data } : item)));
      }
    } else {
      const newItem: ParcelItem = {
        id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        ...data,
      };
      setItems((prev) => [...prev, newItem]);
    }
    handleCloseItemDialog();
  };

  const handleRemoveItem = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const dimFieldStyle = (disabled?: boolean): React.CSSProperties => ({
    ...inputStyle,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'not-allowed' : 'auto',
  });

  return (
    <div
      style={{
        padding: 16,
        marginBottom: 12,
        borderRadius: 8,
        border: '1px solid var(--line)',
        position: 'relative',
        background: 'var(--bg-elev)',
      }}
    >
      <button
        type="button"
        onClick={() => onRemove(id)}
        aria-label="Remove parcel"
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--ink-3)',
          fontSize: 18,
          padding: '2px 6px',
          borderRadius: 4,
        }}
      >
        ×
      </button>

      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 16, color: 'var(--ink-2)' }}>Parcel Details</div>

      <div style={{ display: 'grid', gridTemplateColumns: showPrice ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>
        {showPrice && (
          <Field label="Price">
            <div style={{ position: 'relative' }}>
              <input
                readOnly
                value={prevPriceRef.current}
                style={{ ...inputStyle, paddingRight: 60 }}
              />
              <select
                value={currency}
                onChange={(e) => handleFieldChange('currency', e.target.value as unknown as number)}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  border: 'none',
                  borderLeft: '1px solid var(--line)',
                  borderRadius: '0 6px 6px 0',
                  padding: '0 8px',
                  background: 'var(--bg-tint)',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {expectedCurrency.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </Field>
        )}

        <Controller
          name="quantity"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Field label="Quantity">
              <input
                type="number"
                style={inputStyle}
                value={field.value}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') { field.onChange(''); return; }
                  const n = Number(v);
                  if (!isNaN(n)) { field.onChange(n); handleFieldChange('quantity', n); }
                }}
                min={1}
              />
              {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
            </Field>
          )}
        />

        <Controller
          name="weight"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Field label={`Weight (${weightUnit})`}>
              <div style={{ position: 'relative' }}>
                {weightWarning && (
                  <span
                    title={`Total items weight (${totalItemsWeight.toFixed(2)} ${weightUnit}) exceeds parcel weight`}
                    style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}
                  >
                    ⚠️
                  </span>
                )}
                <input
                  type="number"
                  style={{ ...inputStyle, paddingLeft: weightWarning ? 32 : 12, borderColor: (error || weightWarning) ? 'var(--err)' : undefined }}
                  value={field.value}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') { field.onChange(''); return; }
                    const n = Number(v);
                    if (!isNaN(n)) { field.onChange(n); handleFieldChange('weight', n); }
                  }}
                  min={0}
                  step={0.1}
                />
              </div>
              {(error || weightWarning) && (
                <FieldHint style={{ color: 'var(--err)' }}>
                  {error?.message || `Weight must be at least ${totalItemsWeight.toFixed(2)} ${weightUnit}`}
                </FieldHint>
              )}
            </Field>
          )}
        />
      </div>

      {/* Dimensions */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Dimensions</span>
          <span title={`Enter the dimensions in ${dimensionUnit}. Dimensional weight auto-calculated.`} style={{ cursor: 'help', color: 'var(--ink-4)' }}>?</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {(['length', 'width', 'height'] as const).map((dim) => (
            <Controller
              key={dim}
              name={dim}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Field label={`${dim.charAt(0).toUpperCase() + dim.slice(1)} (${dimensionUnit})`}>
                  <input
                    type="number"
                    disabled={!!areDimensionsDisabled}
                    style={dimFieldStyle(!!areDimensionsDisabled)}
                    value={field.value}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') { field.onChange(''); return; }
                      const n = Number(v);
                      if (!isNaN(n)) { field.onChange(n); handleFieldChange(dim, n); }
                    }}
                    min={0}
                    step={0.1}
                  />
                  {(error || areDimensionsDisabled) && (
                    <FieldHint style={{ color: areDimensionsDisabled ? 'var(--ink-3)' : 'var(--err)' }}>
                      {areDimensionsDisabled ? 'Locked by FedEx packaging type' : error?.message}
                    </FieldHint>
                  )}
                </Field>
              )}
            />
          ))}
        </div>
      </div>

      {/* Weight summary */}
      {(physicalWeight > 0 || dimensionalWeight > 0 || (length > 0 && width > 0 && height > 0)) && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--line)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Physical Weight</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {physicalWeight > 0 ? physicalWeight.toFixed(2) : weight.toFixed(2)} {weightUnit}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Dimensional Weight</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {dimensionalWeight > 0 ? dimensionalWeight.toFixed(2) : (length * width * height / 5000).toFixed(2)} {weightUnit}
              </div>
            </div>
          </div>

          {dimensionalWeight > physicalWeight && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 6,
                borderLeft: '4px solid var(--err)',
                background: 'var(--err-bg)',
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--err)', fontWeight: 600 }}>
                ℹ️ Dimensional Weight will be used for shipping calculation as it is higher than Physical Weight.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Items section */}
      {showPrice && (
        <div style={{ marginTop: 20 }}>
          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', marginBottom: 16 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Package Contents</span>
            <Button size="sm" variant="secondary" onClick={() => handleOpenItemDialog()}>
              + Add Item
            </Button>
          </div>

          {items.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                borderRadius: 6,
                background: 'var(--bg-tint)',
                border: '1px dashed var(--line)',
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>
                No items yet defined. Please add at least one item.
              </div>
              <Button size="sm" variant="blue" onClick={() => handleOpenItemDialog()}>
                Add New Item
              </Button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tint)' }}>
                    {['Description', 'Made In', 'Qty', `Wt (${weightUnit})`, `Price (${currency})`, 'Total', ''].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: h === '' ? 'center' : 'left', fontWeight: 600, borderBottom: '1px solid var(--line)', color: 'var(--ink-2)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '8px 10px' }}>{item.description}</td>
                      <td style={{ padding: '8px 10px' }}>{item.madeIn}</td>
                      <td style={{ padding: '8px 10px' }}>{item.quantity}</td>
                      <td style={{ padding: '8px 10px' }}>{item.weight.toFixed(2)}</td>
                      <td style={{ padding: '8px 10px' }}>{item.price.toFixed(2)}</td>
                      <td style={{ padding: '8px 10px' }}>{(item.price * item.quantity).toFixed(2)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => handleOpenItemDialog(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginRight: 4 }} aria-label="Edit">✏️</button>
                        <button type="button" onClick={() => handleRemoveItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} aria-label="Delete">🗑</button>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--bg-tint)', fontWeight: 600 }}>
                    <td colSpan={2} style={{ padding: '8px 10px' }}>Totals</td>
                    <td style={{ padding: '8px 10px' }}>{items.reduce((s, i) => s + i.quantity, 0)}</td>
                    <td style={{ padding: '8px 10px' }}>{totalItemsWeight.toFixed(2)}</td>
                    <td />
                    <td style={{ padding: '8px 10px' }}>{items.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {weightWarning && items.length > 0 && (
            <Callout tone="warn">
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Weight Warning</div>
                <div style={{ fontSize: 13 }}>
                  The total weight of your items ({totalItemsWeight.toFixed(2)} {weightUnit}) exceeds the specified parcel weight.
                  The parcel weight has been automatically adjusted to match the items' total weight.
                </div>
              </div>
            </Callout>
          )}
        </div>
      )}

      {/* Item Dialog */}
      <Dialog
        open={openItemDialog}
        onOpenChange={(open) => { if (!open) handleCloseItemDialog(); }}
        title={editingItem ? 'Edit Item' : 'Add New Item'}
        maxWidth={560}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={handleCloseItemDialog}>Cancel</Button>
            <Button variant="blue" onClick={handleItemSubmit(onItemSubmit)}>
              {editingItem ? 'Update' : 'Add'} Item
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
          <Controller
            name="description"
            control={itemControl}
            render={({ field, fieldState: { error } }) => (
              <Field label="Item Description">
                <input required style={inputStyle} value={field.value} onChange={(e) => field.onChange(e.target.value)} placeholder="e.g. Laptop computer" />
                {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
              </Field>
            )}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Controller
              name="madeIn"
              control={itemControl}
              render={({ field, fieldState: { error } }) => (
                <Field label="Country of Origin">
                  <select required style={inputStyle} value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                    <option value="">Select country…</option>
                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
                </Field>
              )}
            />

            <Controller
              name="quantity"
              control={itemControl}
              render={({ field, fieldState: { error } }) => (
                <Field label="Quantity">
                  <input type="number" required style={inputStyle} value={field.value} min={1}
                    onChange={(e) => { const v = e.target.value; if (v === '') { field.onChange(''); return; } const n = Number(v); if (!isNaN(n)) field.onChange(n); }} />
                  {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
                </Field>
              )}
            />

            <Controller
              name="weight"
              control={itemControl}
              render={({ field, fieldState: { error } }) => (
                <Field label={`Weight per Item (${weightUnit})`}>
                  <input type="number" required style={inputStyle} value={field.value} min={0} step={0.1}
                    onChange={(e) => { const v = e.target.value; if (v === '') { field.onChange(''); return; } const n = Number(v); if (!isNaN(n)) field.onChange(n); }} />
                  {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
                </Field>
              )}
            />

            <Controller
              name="price"
              control={itemControl}
              render={({ field, fieldState: { error } }) => (
                <Field label={`Price per Item (${currency})`}>
                  <input type="number" required style={inputStyle} value={field.value} min={0} step={0.01}
                    onChange={(e) => { const v = e.target.value; if (v === '') { field.onChange(''); return; } const n = Number(v); if (!isNaN(n)) field.onChange(n); }} />
                  {error && <FieldHint style={{ color: 'var(--err)' }}>{error.message}</FieldHint>}
                </Field>
              )}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ParcelForm;
